import {
  CheckOutlined,
  CloseOutlined,
  CopyFilled,
  DeleteOutlined,
  EditFilled,
  EllipsisOutlined,
  FilterOutlined,
  PauseOutlined,
  PlusOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Input, Card, message, Select, Dropdown, Tooltip, Col, Row, Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import ErrorResult from '../components/ErrorResult';
import Filter from '../components/Filter';
import OpportunityTable from '../components/OpportunityTable';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import PermissionChecker from '../components/PermissionChecker';
import SearchParam from '../components/SearchParam';
import VacancyActionsModal, { VacancyActions } from '../components/VacancyModal/VacancyActions';
import usePermissionCheckerQuery from '../hooks/usePermissionCheckerQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { withRouteChange } from '../services/APM';
import { JobPostingDetail, JobPostingCollectionParams, JobPostingCollection } from '../types';
import { useVixtingParams } from '../utils';

const STALE_TIME = 1000 * 60 * 5;
export interface JobPostingActionsProps {
  jobPosting: JobPostingDetail;
  action: VacancyActions;
  open?: boolean;
}

type TableActionsProps = {
  record: JobPostingCollection;
  canEdit?: boolean;
  onViewClick: () => void;
  onEditClick: () => void;
  onReopenClick: () => void;
  onFinalizeClick: () => void;
  onSuspendClick: () => void;
  onCancelClick: () => void;
  onCloneClick: () => void;
  onDeleteClick: () => void;
};

function TableActions({
  record,
  canEdit,
  onViewClick,
  onEditClick,
  onReopenClick,
  onFinalizeClick,
  onSuspendClick,
  onCancelClick,
  onCloneClick,
  onDeleteClick,
}: TableActionsProps) {
  const hasCreateAndEditVacanciesPermission = usePermissionCheckerQuery([
    'create_and_edit_vacancies',
  ]);

  return (
    <Dropdown
      menu={{
        onClick: ({ key }) => {
          switch (key) {
            case 'view':
              onViewClick();
              break;
            case 'edit':
              canEdit && onEditClick();
              break;
            case 'reopen':
              canEdit && onReopenClick();
              break;
            case 'finalize':
              canEdit && onFinalizeClick();
              break;
            case 'suspend':
              canEdit && onSuspendClick();
              break;
            case 'cancel':
              canEdit && onCancelClick();
              break;
            case 'clone':
              canEdit && onCloneClick();
              break;
            case 'delete':
              canEdit && onDeleteClick();
              break;
          }
        },
        items: [
          { key: 'view', icon: <UserOutlined />, label: 'Analisar candidatos' },
          ...(canEdit
            ? [
                ...(hasCreateAndEditVacanciesPermission.data
                  ? [
                      {
                        key: 'edit',
                        icon: <EditFilled />,
                        label: 'Editar',
                      },
                    ]
                  : []),
                ...(record.status !== 'IN_PROGRESS'
                  ? record.status !== 'FINALIZED'
                    ? [
                        {
                          key: 'reopen',
                          icon: <UndoOutlined />,
                          label: 'Reabrir',
                        },
                      ]
                    : []
                  : [
                      {
                        key: 'finalize',
                        disabled: !record.filled_positions,
                        icon: <CheckOutlined />,
                        label: (
                          <Tooltip
                            title={
                              !record.filled_positions
                                ? 'Essa vaga não pode ser concluída, pois não tem nenhum candidato contratado.'
                                : ''
                            }
                          >
                            Concluir
                          </Tooltip>
                        ),
                      },
                      { key: 'suspend', icon: <PauseOutlined />, label: 'Congelar' },
                      {
                        key: 'cancel',
                        disabled: !!record.filled_positions,
                        icon: <CloseOutlined />,
                        label: (
                          <Tooltip
                            title={
                              record.filled_positions
                                ? 'Essa vaga não pode ser cancelada, pois já existe candidato contratado.'
                                : ''
                            }
                          >
                            Cancelar
                          </Tooltip>
                        ),
                      },
                    ]),
                { key: 'clone', icon: <CopyFilled />, label: 'Clonar' },
                {
                  key: 'delete',
                  danger: true,
                  disabled: (record.total_recruitment_process_count as number) > 0,
                  icon: <DeleteOutlined />,
                  label: (
                    <Tooltip
                      title={
                        (record.total_recruitment_process_count as number) > 0
                          ? 'Essa vaga não pode ser excluída, pois possui candidatos.'
                          : ''
                      }
                    >
                      Excluir
                    </Tooltip>
                  ),
                },
              ]
            : []),
        ],
      }}
    >
      <Button type="text" icon={<EllipsisOutlined />} aria-label={`Ações para ${record.name}`} />
    </Dropdown>
  );
}

const Opportunities = () => {
  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();
  const [searchParams, setSearchParams] = useSearchParams({ status: 'IN_PROGRESS' });
  const [selected, setSelected] = useState<JobPostingActionsProps>();

  useEffect(() => {
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const vixtingParams = useVixtingParams(searchParams, 'vixtingjobposting');

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => {
    return vixtingParams.hasParams;
  });

  const jobPostings = useAPIQuery([
    '/job-postings/',
    {
      params: {
        search: searchParams.get('search') || undefined,
        status: (searchParams.get('status') as JobPostingCollectionParams['status']) || undefined,
        ...vixtingParams.apiParams,
        page: parseInt(searchParams.get('page') || '1', 10),
        page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        expand:
          'active_subscribers_count,total_recruitment_process_count,inactive_count,in_progress_count,current_recruiter_type',
      },
    },
  ]);

  const viewAllVacanciesChecker = usePermissionCheckerQuery(['view_all_vacancies']);

  const { mutate: reopenVacancyMutation } = useAPIMutation(
    async (data: { jobPostingId?: JobPostingDetail['id'] }, api) => {
      await api.post(`job-postings/${data.jobPostingId}/reopen/`).then(() => {
        message.success('A vaga foi reaberta!');
      });

      jobPostings.refetch();
    }
  );

  const pagination = useMemo<React.ComponentProps<typeof OpportunityTable>['pagination']>(
    () => ({
      showTotal: (total) => {
        return `Total: ${total} vagas`;
      },
      total: jobPostings.data?.count,
      onChange: (page, pageSize) => {
        searchParams.set('page', page.toString());
        searchParams.set('pageSize', pageSize.toString());
        setSearchParams(searchParams);
      },
      current: parseInt(searchParams.get('page') || '1', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
      pageSizeOptions: [10, 20],
    }),
    [jobPostings.data?.count, searchParams, setSearchParams]
  );

  const actionsRender = useCallback<React.ComponentProps<typeof OpportunityTable>['actionsRender']>(
    (_value, record) => (
      <TableActions
        record={record}
        canEdit={viewAllVacanciesChecker.data || record.current_recruiter_type !== 'OBSERVER'}
        onViewClick={() =>
          navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id: record.id } })
        }
        onEditClick={() =>
          navigate({
            pathname: '/:tenant/opportunities/:id/change/',
            params: { tenant, id: record.id },
          })
        }
        onReopenClick={() => {
          reopenVacancyMutation({
            jobPostingId: record.id,
          });
        }}
        onFinalizeClick={() =>
          setSelected({
            jobPosting: record,
            action: 'FINALIZED',
            open: true,
          })
        }
        onSuspendClick={() =>
          setSelected({
            jobPosting: record,
            action: 'SUSPENDED',
            open: true,
          })
        }
        onCancelClick={() =>
          setSelected({
            jobPosting: record,
            action: 'CANCELED',
            open: true,
          })
        }
        onCloneClick={() =>
          setSelected({
            jobPosting: record,
            action: 'CLONE',
            open: true,
          })
        }
        onDeleteClick={() =>
          setSelected({
            jobPosting: record,
            action: 'DELETE',
            open: true,
          })
        }
      />
    ),
    [navigate, reopenVacancyMutation, tenant, viewAllVacanciesChecker.data]
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar empresas da vixting!');
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar filiais da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar unidades de negócio da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar cargos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar departamentos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingAllDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const vixtingColSpan = config.data?.enableVixtingUnitBusiness ? 5 : 6;

  return (
    <Page>
      {selected?.jobPosting && (
        <VacancyActionsModal
          {...selected}
          onOk={() => {
            setSelected({ ...selected, open: false });
            jobPostings.refetch();
          }}
          onCancel={() => setSelected({ ...selected, open: false })}
        />
      )}
      <PageTitle
        title="Vagas"
        extra={[
          <PermissionChecker key="newVacancy" requiredPermissions={['create_and_edit_vacancies']}>
            <Link to="new">
              <Button icon={<PlusOutlined />}>Nova vaga</Button>
            </Link>
          </PermissionChecker>,
        ]}
      />
      <ErrorResult error={jobPostings.error}>
        <PageContent>
          <Card>
            <Filter>
              <Col span={24}>
                <Form layout="vertical">
                  <Row gutter={[12, 24]} align="middle">
                    <Col flex={1}>
                      <Form.Item label="Buscar">
                        <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
                          <Input.Search allowClear placeholder="Nome ou código da vaga" />
                        </SearchParam>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item label="Status">
                        <SearchParam
                          name="status"
                          defaultValue=""
                          onSetSearchParams={{ page: '1' }}
                        >
                          <Select
                            allowClear
                            options={[
                              { value: 'IN_PROGRESS', label: 'Abertas' },
                              { value: 'FINALIZED', label: 'Concluídas' },
                              { value: 'SUSPENDED', label: 'Congeladas' },
                              { value: 'CANCELED', label: 'Canceladas' },
                              { value: '', label: 'Todas' },
                            ]}
                          />
                        </SearchParam>
                      </Form.Item>
                    </Col>
                    <Col>
                      {config.data?.enableVixtingAdmission && (
                        <Form.Item label>
                          <Button
                            type={
                              showAdvancedFilters || vixtingParams.hasParams ? 'primary' : 'default'
                            }
                            ghost={showAdvancedFilters || vixtingParams.hasParams}
                            icon={<FilterOutlined />}
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                          >
                            Filtros avançados
                          </Button>
                        </Form.Item>
                      )}
                    </Col>
                    <Col>
                      <Form.Item label>
                        <Button type="default" onClick={() => setSearchParams({ page: '1' })}>
                          Limpar Filtros
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                  {config.data?.enableVixtingAdmission && showAdvancedFilters && (
                    <>
                      <Row gutter={[8, 8]}>
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Empresa">
                            <SearchParam
                              name={vixtingParams.queryParams.COMPANY}
                              onSetSearchParams={{
                                page: '1',
                                [vixtingParams.queryParams.JOB_ROLE]: '',
                                [vixtingParams.queryParams.DEPARTMENT]: '',
                                [vixtingParams.queryParams.AFFILIATE]: '',
                                [vixtingParams.queryParams.UNIT_BUSINESS]: '',
                              }}
                            >
                              <Select
                                showSearch
                                allowClear
                                placeholder="Empresa"
                                loading={vixtingCompaniesPages.isLoading}
                                data-testid="vixting-company-select"
                                options={vixtingCompanies.map((company) => ({
                                  key: company.id,
                                  label: company.name,
                                  value: company.id,
                                  'data-testid': `company-${company.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Filial">
                            <SearchParam
                              name={vixtingParams.queryParams.AFFILIATE}
                              onSetSearchParams={{ page: '1' }}
                            >
                              <Select
                                allowClear
                                showSearch
                                loading={vixtingAffiliatesPages.isLoading}
                                disabled={
                                  vixtingAffiliatesPages.isLoading || !vixtingParams.params.company
                                }
                                placeholder="Filial"
                                data-testid="vixting-affiliate-select"
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                }
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vixtingAffiliates.map((affiliate) => ({
                                  key: affiliate.id,
                                  label: affiliate.fantasyName,
                                  value: affiliate.id,
                                  'data-testid': `affiliate-${affiliate.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                        {config.data?.enableVixtingUnitBusiness && (
                          <Col span={4}>
                            <Form.Item label="Unidade de Negócio">
                              <SearchParam
                                name={vixtingParams.queryParams.UNIT_BUSINESS}
                                onSetSearchParams={{ page: '1' }}
                              >
                                <Select
                                  allowClear
                                  showSearch
                                  loading={vixtingUnitBusinessesPages.isLoading}
                                  disabled={
                                    vixtingUnitBusinessesPages.isLoading ||
                                    !vixtingParams.params.company
                                  }
                                  placeholder="Unidade de Negócio"
                                  data-testid="vixting-unit-business-select"
                                  filterSort={(optionA, optionB) =>
                                    (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                  }
                                  filterOption={(input, option) =>
                                    (option?.label ?? '')
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  options={vixtingUnitBusinesses.map((unitBusiness) => ({
                                    key: unitBusiness.id,
                                    label: unitBusiness.name,
                                    value: unitBusiness.id.toString(),
                                    'data-testid': `unit-business-${unitBusiness.id}`,
                                  }))}
                                />
                              </SearchParam>
                            </Form.Item>
                          </Col>
                        )}
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Cargo">
                            <SearchParam
                              name={vixtingParams.queryParams.JOB_ROLE}
                              onSetSearchParams={{ page: '1' }}
                            >
                              <Select
                                showSearch
                                allowClear
                                loading={vixtingJobRolesPages.isLoading}
                                disabled={
                                  vixtingJobRolesPages.isLoading || !vixtingParams.params.company
                                }
                                placeholder="Cargo"
                                data-testid="vixting-job-role-select"
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                }
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vixtingJobRoles.map((jobRole) => ({
                                  key: jobRole.id,
                                  label: jobRole.name,
                                  value: jobRole.id.toString(),
                                  'data-testid': `jobRole-${jobRole.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Departamento">
                            <SearchParam
                              name={vixtingParams.queryParams.DEPARTMENT}
                              onSetSearchParams={{ page: '1' }}
                            >
                              <Select
                                showSearch
                                allowClear
                                loading={vixtingDepartmentsPages.isLoading}
                                disabled={
                                  !vixtingParams.params.company || vixtingDepartmentsPages.isLoading
                                }
                                placeholder="Departamento"
                                data-testid="vixting-department-select"
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                }
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vixtingAllDepartments.map((department) => ({
                                  key: department.id,
                                  label: department.name,
                                  value: department.id.toString(),
                                  'data-testid': `department-${department.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </Form>
              </Col>
            </Filter>
            <OpportunityTable
              loading={jobPostings.isFetching}
              dataSource={jobPostings.data?.results}
              pagination={pagination}
              actionsRender={actionsRender}
            />
          </Card>
        </PageContent>
      </ErrorResult>
    </Page>
  );
};

export default withRouteChange()(Opportunities);
